// Dashboard announcements: the feed, the caller's edit rights, the per-user "seen" stamp
// and the unseen count behind the red Dashboard nav pip. Live-updated by the ids-only
// system.global WS ping (refetch), refreshed on login/profile switch and on a slow
// interval as the fallback (the work-plan-mc-store shape).
//
// The seen stamp is an account setting (user-settings sync core, key "dashboardSeenAt",
// value {value: "<ISO>"}), so "what's new to me" travels across devices. Unseen = posts
// created after the stamp; a missing stamp means every post is unseen (first-ever visit).
//
// Mid-session notify: each refresh diffs the fetched ids against the known set — seeded on
// the FIRST successful fetch per identity, so neither login nor profile switch blasts the
// backlog — and hands genuinely-new posts to announcement-notifications (which applies the
// own-post / DND / viewing-dashboard rules).

import { fetchDashboardPost, fetchDashboardPosts } from "@/lib/api/dashboard-posts";
import { getAuthToken, getAuthUser, subscribeAuthStateChange } from "@/lib/api/token-store";
import { normalizeReactionSummary } from "@/lib/dashboard/reactions-helpers";
import { notifyNewAnnouncements } from "@/lib/notifications/announcement-notifications";
import { subscribeDashboardPostEvents } from "@/lib/realtime/chat-realtime-service";
import {
  USER_SETTING_KEYS,
  readUserSetting,
  subscribeUserSettingsActivation,
  writeUserSetting,
} from "@/lib/stores/user-settings-sync";

const REFRESH_INTERVAL_MS = 120000;
const WS_REFRESH_DEBOUNCE_MS = 800;
// Calls to markDashboardSeen within this window count as ONE visit and return the same
// prior stamp: StrictMode double-fires the pane's visit effect in dev, and a rapid
// hide/re-show flip should not wipe the NEW badges the first fire just computed.
const VISIT_GRACE_MS = 5000;

let state = Object.freeze({ posts: [], canEdit: false, loaded: false });
let started = false;
// Identity the current posts belong to; a switch resets the store before refetching.
let loadedForUid = "";
// null = not yet seeded for this identity (first successful fetch seeds silently).
let knownPostIds = null;
let wsDebounceTimer = null;
// Realtime pings accumulate here between debounce flushes: the ids of posts whose engagement
// changed, plus a flag for anything that needs the whole feed back.
let pendingPostIds = new Set();
let needsFullRefresh = false;
// { prior, at } of the most recent markDashboardSeen — the visit-grace anchor.
let lastVisit = null;
const listeners = new Set();
const unseenListeners = new Set();

/**
 * The recent commenters behind a collapsed thread's facepile. Server-bounded (three), so this
 * only has to clean the entries — an id for the avatar hue, a name for the initials.
 */
function normalizeCommentAuthors(value) {
  if (!Array.isArray(value)) return [];
  const out = [];
  for (const entry of value) {
    const id = String(entry?.id || "").trim();
    const name = String(entry?.name || "").trim();
    if (!id && !name) continue;
    out.push({ id, name });
  }
  return out;
}

/**
 * The client-side post shape. Note it is an ALLOW-LIST: anything the server sends that isn't
 * named here is dropped, so a new field has to be added in both places to survive.
 */
export function normalizePost(value) {
  if (!value || typeof value !== "object") return null;
  const id = String(value.id || "").trim();
  if (!id) return null;
  const commentCount = Number(value.commentCount);
  return {
    id,
    title: String(value.title || "").trim(),
    body: String(value.body || ""),
    authorId: String(value.authorId || "").trim(),
    authorName: String(value.authorName || "").trim(),
    reactions: normalizeReactionSummary(value.reactions),
    commentCount: Number.isFinite(commentCount) && commentCount > 0 ? Math.trunc(commentCount) : 0,
    // Both describe a thread the feed is NOT carrying, so a collapsed post can still show
    // "2 comments · last 3h ago" over a facepile without fetching anything.
    lastCommentAt: String(value.lastCommentAt || "").trim(),
    commentAuthors: normalizeCommentAuthors(value.commentAuthors),
    createdAt: String(value.createdAt || "").trim(),
    updatedAt: String(value.updatedAt || "").trim(),
  };
}

/**
 * Swap one post into a feed, preserving order. Returns the SAME array reference when the id
 * isn't on the page — a ping about a post outside the 100-post window must not churn every
 * subscriber.
 */
export function replacePostInFeed(posts, nextPost) {
  const list = Array.isArray(posts) ? posts : [];
  if (!nextPost?.id) return list;
  const index = list.findIndex((post) => post.id === nextPost.id);
  if (index === -1) return list;
  const next = list.slice();
  next[index] = nextPost;
  return next;
}

/**
 * Decide what a batch of realtime pings costs us.
 *
 * Reactions and comments only ever change ONE post, so a burst about a single post is worth a
 * single-post GET instead of pulling the whole feed. Anything that can change which posts
 * exist — created/deleted/updated, or an action we don't recognise — needs the full refetch,
 * because the NEW-badge threshold and the new-announcement notification both key off the id
 * set. Several posts at once falls back to the full refetch rather than N round trips.
 */
export function planDashboardRefresh({ pendingPostIds, needsFullRefresh }) {
  const ids = pendingPostIds instanceof Set ? [...pendingPostIds] : [];
  if (needsFullRefresh || ids.length !== 1 || !ids[0]) return { mode: "full" };
  return { mode: "post", postId: ids[0] };
}

// Only these two leave the id set untouched, so only these two are patchable. Everything
// else — including an action a future server adds — takes the safe path.
const PATCHABLE_ACTIONS = new Set(["reacted", "commented"]);

function setState(next) {
  state = Object.freeze(next);
  for (const listener of listeners) {
    try {
      listener(state);
    } catch {
      /* ignore listener failures */
    }
  }
  emitUnseen();
}

function emitUnseen() {
  const count = getDashboardUnseenCount();
  for (const listener of unseenListeners) {
    try {
      listener(count);
    } catch {
      /* ignore listener failures */
    }
  }
}

/**
 * Posts created strictly after `seenAt`. A missing/invalid stamp counts every post
 * (first-ever visit); posts without a parseable createdAt never count. Pure — exported
 * for tests.
 */
export function computeUnseenCount(posts, seenAt) {
  const list = Array.isArray(posts) ? posts : [];
  const seenMs = Date.parse(String(seenAt || ""));
  if (!Number.isFinite(seenMs)) {
    return list.length;
  }
  let count = 0;
  for (const post of list) {
    const createdMs = Date.parse(String(post?.createdAt || ""));
    if (Number.isFinite(createdMs) && createdMs > seenMs) {
      count += 1;
    }
  }
  return count;
}

/** The caller's stored seen stamp (ISO string) or undefined. */
export function getDashboardSeenAt() {
  const stored = readUserSetting(USER_SETTING_KEYS.DASHBOARD_SEEN_AT);
  const value = String(stored?.value || "").trim();
  return value || undefined;
}

export async function refreshDashboardPosts() {
  if (!getAuthToken()) {
    loadedForUid = "";
    knownPostIds = null;
    setState({ posts: [], canEdit: false, loaded: false });
    return state;
  }
  const requestUserId = String(getAuthUser()?.id || "").trim();
  try {
    const response = await fetchDashboardPosts();
    // A profile switch mid-flight: drop the stale result, the new identity's own refresh
    // is already on its way (auth subscription).
    if (String(getAuthUser()?.id || "").trim() !== requestUserId) {
      return state;
    }
    const posts = response.items.map(normalizePost).filter(Boolean);
    if (knownPostIds !== null && loadedForUid === requestUserId) {
      const freshPosts = posts.filter((post) => !knownPostIds.has(post.id));
      if (freshPosts.length > 0) {
        notifyNewAnnouncements(freshPosts);
      }
    }
    knownPostIds = new Set(posts.map((post) => post.id));
    loadedForUid = requestUserId;
    setState({ posts, canEdit: response.canEdit, loaded: true });
  } catch {
    /* keep the previous state; the next tick retries */
  }
  return state;
}

/**
 * Refetch ONE post and swap it into the feed in place. Used for reaction and comment pings,
 * which can't change which posts exist.
 *
 * Because this never touches `knownPostIds`, the new-announcement notification path stays
 * post-only by construction — engagement can't accidentally start firing toasts.
 */
export async function refreshDashboardPost(postId) {
  const id = String(postId || "").trim();
  if (!id || !getAuthToken()) return state;
  const requestUserId = String(getAuthUser()?.id || "").trim();
  try {
    const payload = await fetchDashboardPost(id);
    // A profile switch mid-flight: drop the stale result, like refreshDashboardPosts.
    if (String(getAuthUser()?.id || "").trim() !== requestUserId) return state;
    const post = normalizePost(payload);
    if (!post) return state;
    const posts = replacePostInFeed(state.posts, post);
    if (posts !== state.posts) setState({ ...state, posts });
  } catch {
    // The post may have just been deleted, or the request lost a race — the full refetch on
    // the next tick realigns the feed either way.
  }
  return state;
}

/**
 * Merge a partial change into one post immediately, without a round trip — the optimistic
 * half of a reaction toggle. The server's response (or the realtime echo) reconciles it.
 */
export function patchDashboardPost(postId, patch) {
  const id = String(postId || "").trim();
  if (!id || !patch) return state;
  const existing = state.posts.find((post) => post.id === id);
  if (!existing) return state;
  const posts = replacePostInFeed(state.posts, { ...existing, ...patch });
  if (posts !== state.posts) setState({ ...state, posts });
  return state;
}

/**
 * Stamp "the user just looked at the dashboard": returns the PRIOR stamp (undefined on a
 * first-ever visit) for the pane's NEW-badge threshold, then moves the stamp to now. The
 * write rides the settings sync core (debounced PUT + dirty retry) — a failed upload keeps
 * the optimistic local value for the session and self-heals on the next visit.
 */
export function markDashboardSeen() {
  const now = Date.now();
  const prior =
    lastVisit && now - lastVisit.at < VISIT_GRACE_MS ? lastVisit.prior : getDashboardSeenAt();
  lastVisit = { prior, at: now };
  writeUserSetting(USER_SETTING_KEYS.DASHBOARD_SEEN_AT, { value: new Date(now).toISOString() });
  emitUnseen();
  return prior;
}

function handleAuthChange() {
  const uid = String(getAuthUser()?.id || "").trim();
  if (uid !== loadedForUid) {
    // New identity: never leak the previous user's posts, notify off their known set, or
    // hand their visit anchor to the incoming user.
    knownPostIds = null;
    lastVisit = null;
    setState({ posts: [], canEdit: false, loaded: false });
  }
  void refreshDashboardPosts();
}

function ensureStarted() {
  if (started || typeof window === "undefined") return;
  started = true;
  void refreshDashboardPosts();
  // Never cleared — the store lives for the app session, like the other module stores.
  setInterval(() => void refreshDashboardPosts(), REFRESH_INTERVAL_MS);
  subscribeAuthStateChange(handleAuthChange);
  // The seen stamp can change under us (login preload pulled another device's visit).
  subscribeUserSettingsActivation(emitUnseen);
  subscribeDashboardPostEvents((event) => {
    const action = String(event?.action || "").trim();
    const postId = String(event?.postId || "").trim();
    if (PATCHABLE_ACTIONS.has(action) && postId) {
      pendingPostIds.add(postId);
    } else {
      // Post created/edited/deleted — or an action this client doesn't know. Either way the
      // set of posts may have changed, so take the whole feed back.
      needsFullRefresh = true;
    }
    if (wsDebounceTimer) clearTimeout(wsDebounceTimer);
    wsDebounceTimer = setTimeout(() => {
      wsDebounceTimer = null;
      const plan = planDashboardRefresh({ pendingPostIds, needsFullRefresh });
      pendingPostIds = new Set();
      needsFullRefresh = false;
      if (plan.mode === "post") void refreshDashboardPost(plan.postId);
      else void refreshDashboardPosts();
    }, WS_REFRESH_DEBOUNCE_MS);
  });
}

export function getDashboardPostsState() {
  ensureStarted();
  return state;
}

export function subscribeDashboardPosts(listener) {
  if (typeof listener !== "function") return () => {};
  ensureStarted();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Unseen announcements behind the red Dashboard nav pip (0 until posts load). */
export function getDashboardUnseenCount() {
  ensureStarted();
  return computeUnseenCount(state.posts, getDashboardSeenAt());
}

export function subscribeDashboardUnseenCount(listener) {
  if (typeof listener !== "function") return () => {};
  ensureStarted();
  unseenListeners.add(listener);
  return () => {
    unseenListeners.delete(listener);
  };
}
